/**
 * Per-services runtime state of the note lane (W2-03 note-lane).
 *
 * The lane column def (timeline factory), the registered commands and the Chart timeline panel run in different
 * places but act on the same lanes: the mounted timeline host, the lane views by instance id, the last pointer
 * position over a lane ("at the mouse" commands), the hit under the last right click (context-menu commands), the
 * live gesture overlays (move ghost, eraser trail, transform box handle), the hover tooltip and the package's own
 * dialogs. `laneSession(services)` returns the one session of a services object (WeakMap), created on first use.
 *
 * Also here: document helpers shared by the commands (tempo map, playhead beat, snapping, note indexes per line
 * cached on the document's version counters, line labels).
 */

import { Beats, beatFromF64Snapped } from '../../core/time/beat';
import { createTempoMap } from '../../core/time/tempo';
import { formatBeat, formatSec } from '../../core/time/beatSyntax';
import type { Beat, TempoMap } from '../../core/time/types';
import type { MessageRef, Unsubscribe } from '../../core/util/types';
import type { ChartDoc, DocIndexes, KeyId, LineId, MarkerId, Note, NoteId, NoteKind } from '../../core/model/types';
import type { ClipboardPayload } from '../../core/commands/types';
import type { AppServices } from '../../app/registry/types';
import type { DocStore, EditorStore, PlacementState, SnapState } from '../../state/types';
import { asTimelineHostController, type TimelineHostController } from '../../render/timeline/TimelineHost';
import { buildLaneNoteIndex, EMPTY_NOTE_INDEX, type LaneNoteIndex } from '../../render/timeline/laneHit';
import type { LaneView } from '../../render/timeline/laneColumn';
import { snapToXGrid } from '../../render/timeline/laneDraw';
import type { TransformHandle } from '../../render/timeline/laneDraw';
import { m } from './noteLane.messages';

export type LaneSides = 'both' | 'above' | 'below';

/** The last pointer position over a lane. */
export interface LanePointerInfo {
  readonly instanceId: string;
  readonly line: LineId | null;
  /** Beat (f64) and RPE X under the pointer. */
  readonly beat: number;
  readonly x: number;
  /** Lane px (for redraws after scrolling). */
  readonly px: number;
  readonly py: number;
  readonly alt: boolean;
  readonly ctrl: boolean;
  readonly shift: boolean;
}

/** What the last right click hit (context-menu commands act on it). */
export interface LaneContextHit {
  readonly line: LineId | null;
  readonly beat: number;
  readonly x: number;
  readonly note: NoteId | null;
}

/** Overlay state of the running pointer gesture. */
export interface LaneGestureOverlay {
  /** Ghost transform of a large drag (render only until release). */
  move: { readonly ids: ReadonlySet<NoteId>; readonly dBeat: number; readonly dx: number } | null;
  /** Hold being drawn with the Hold tool: start beat, end beat (f64), X. */
  holdDraw: { readonly line: LineId; readonly start: number; readonly end: number; readonly x: number } | null;
  eraser: Array<readonly [number, number]> | null;
  /** Transform box handle being dragged or hovered. */
  boxHandle: TransformHandle | null;
}

/** Tooltip of the hovered note (shown after a delay by the panel). */
export interface LaneTooltip {
  readonly note: NoteId;
  readonly clientX: number;
  readonly clientY: number;
}

/** Package-private dialogs, rendered by LaneShellHost (mounted in every editor workspace). */
export type LaneDialog =
  | { readonly kind: 'insertTime'; readonly at: Beat; readonly resolve: (r: InsertTimeChoice | null) => void }
  | {
      readonly kind: 'removeTime';
      readonly from: Beat;
      readonly to: Beat;
      readonly resolve: (r: { readonly scope: TimeScope } | null) => void;
    }
  | {
      readonly kind: 'renameMarker';
      readonly marker: MarkerId;
      readonly name: string;
      readonly resolve: (r: string | null) => void;
    };

export type TimeScope = 'all' | readonly LineId[];

export interface InsertTimeChoice {
  readonly length: Beat;
  readonly scope: TimeScope;
}

/** Anchor and X offset of the lane's own paste ghost (see LaneSession.pasteChoice). */
export interface PasteGhostChoice {
  readonly placement: Extract<PlacementState, { kind: 'paste' }>;
  readonly anchor: 'mouse' | 'playhead';
  readonly xShift: number;
}

/** "Paste the rest as a new layer" offers, by token. */
export interface PendingRest {
  readonly payload: ClipboardPayload;
  readonly anchor: Beat;
  readonly targetLine: LineId;
  /** The original paste was mirrored (the rest keeps its sign). */
  readonly mirror: boolean;
}

export interface LaneSession {
  /** User settings (stable for the app's lifetime). */
  readonly settings: AppServices['settings'];
  /** The mounted timeline host (the Chart timeline panel attaches it). */
  host: TimelineHostController | null;
  readonly views: Map<string, LaneView>;
  pointer: LanePointerInfo | null;
  /** Last RPE X the pointer had over a lane (Shift+Q at the playhead uses it when the mouse is elsewhere). */
  lastX: number;
  context: LaneContextHit | null;
  /** Time gutter right click: the beat and the marker under it. */
  gutter: { readonly beat: number; readonly marker: MarkerId | null } | null;
  /** The last contextmenu event inside the timeline (client position for menus). */
  lastContextEvent: MouseEvent | null;
  /** Last pointer position over the timeline in client px (tooltips, menus opened from the keyboard). */
  client: { readonly x: number; readonly y: number } | null;
  sides: LaneSides;
  /** RPE preset aliases held down: Z (move) and D (delete). */
  heldZ: boolean;
  heldD: boolean;
  readonly gesture: LaneGestureOverlay;
  /**
   * What this package chose for the paste ghost it put up: the anchor (a paste special 'mouse' / 'playhead' choice,
   * else the "Paste anchor" setting) and the X offset (paste special); the editor placement has no fields for them.
   * Keyed by the placement object this package set (M, Tab and the brackets carry it over to their new placement),
   * so a ghost another package puts up (W2-11's paste special hands over through the placement state) never inherits
   * them: such a ghost follows the mouse with no extra offset.
   */
  pasteChoice: PasteGhostChoice | null;
  tooltip: LaneTooltip | null;
  /** Key of the tool hint last sent to the hint bar (null after a gesture replaced it). */
  hintKey: string | null;
  dialog: LaneDialog | null;
  readonly rests: Map<number, PendingRest>;
  nextRestToken: number;
  /** Notify React subscribers (tooltip, dialogs, header state). */
  emit(): void;
  subscribe(cb: () => void): Unsubscribe;
  /** Redraw lane layers (all lanes). */
  redraw(layers?: readonly ('static' | 'content' | 'overlay')[]): void;
}

/**
 * Sessions are keyed by the editor store: it lives as long as the app, and a CommandContext (a copy of the services
 * with the trigger context) shares it with the services the panels hold, so commands and panels see one session.
 */
const sessions = new WeakMap<object, LaneSession>();

export function laneSession(services: Pick<AppServices, 'editor' | 'settings'>): LaneSession {
  const hit = sessions.get(services.editor);
  if (hit !== undefined) return hit;
  const listeners = new Set<() => void>();
  const settings = services.settings;
  const s: LaneSession = {
    settings,
    host: null,
    views: new Map(),
    pointer: null,
    lastX: 0,
    context: null,
    gutter: null,
    lastContextEvent: null,
    client: null,
    sides: readSides(services),
    heldZ: false,
    heldD: false,
    gesture: { move: null, holdDraw: null, eraser: null, boxHandle: null },
    pasteChoice: null,
    tooltip: null,
    hintKey: null,
    dialog: null,
    rests: new Map(),
    nextRestToken: 1,
    emit() {
      for (const cb of [...listeners]) cb();
    },
    subscribe(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    redraw(layers = ['content', 'overlay']) {
      const host = s.host;
      if (host === null) return;
      for (const id of s.views.keys()) for (const layer of layers) host.invalidate(id, layer);
    },
  };
  sessions.set(services.editor, s);
  return s;
}

/**
 * The last pointer over a lane with its beat recomputed for the current view: the wheel scrolls time under a
 * resting pointer (R ... R holds and paste ghosts keep following it while scrolling).
 */
export function livePointer(s: LaneSession): LanePointerInfo | null {
  const p = s.pointer;
  if (p === null) return null;
  const axis = s.views.get(p.instanceId)?.axis ?? null;
  return axis === null ? p : { ...p, beat: axis.beatAtY(p.py) };
}

/** The session's host (the Chart timeline panel attaches it), else the services' timeline as the controller. */
export function hostOf(s: LaneSession, services?: Pick<AppServices, 'timeline'>): TimelineHostController | null {
  if (s.host !== null) return s.host;
  const t = services?.timeline ?? null;
  return t === null ? null : asTimelineHostController(t);
}

const SIDES_EXT = 'noteLane.sides';

/** The sides filter preference (settings ext 'noteLane.sides'). */
export function readSides(services: Pick<AppServices, 'settings'>): LaneSides {
  try {
    const v = services.settings.getExt(SIDES_EXT);
    return v === 'above' || v === 'below' ? v : 'both';
  } catch {
    return 'both';
  }
}

/** Gesture sub-actions in the hint bar (a gesture's hints replace the tool hint until the next hover). */
export function gestureHints(
  services: Pick<AppServices, 'ui' | 'editor' | 'settings'>,
  items: Parameters<AppServices['ui']['setGestureHints']>[0],
): void {
  laneSession(services).hintKey = null;
  services.ui.setGestureHints(items);
}

export function setSides(s: LaneSession, sides: LaneSides): void {
  s.sides = sides;
  try {
    s.settings.setExt(SIDES_EXT, sides);
  } catch {
    // Settings unavailable (tests): the session keeps the value.
  }
  s.redraw(['content', 'overlay']);
  s.emit();
}

// ---------------------------------------------------------------------------------------------------------------
// Document helpers
// ---------------------------------------------------------------------------------------------------------------

export function ref(key: string, params?: Readonly<Record<string, string | number>>): MessageRef {
  return params === undefined ? { key } : { key, params };
}

const tempoCache = new WeakMap<object, { readonly v: number; readonly map: TempoMap }>();

/** Tempo map of the open document (the engine's when there is one; cached on the BPM table version otherwise). */
export function tempoOf(services: Pick<AppServices, 'store' | 'engine'>): TempoMap | null {
  const store = services.store;
  if (store === null) return null;
  const engine = services.engine;
  if (engine !== null) {
    try {
      return engine.tempo;
    } catch {
      // fall through to the document
    }
  }
  return tempoOfStore(store);
}

export function tempoOfStore(store: Pick<DocStore, 'doc' | 'version'>): TempoMap {
  const v = store.version('bpm');
  const hit = tempoCache.get(store);
  if (hit?.v === v) return hit.map;
  const points = [...store.doc.bpm.values()].sort((a, b) => Beats.cmp(a.t, b.t));
  const map = createTempoMap(points.map((p) => ({ bpm: p.bpm, startTime: Beats.toTriple(p.t) })));
  tempoCache.set(store, { v, map });
  return map;
}

/** Current playhead in seconds (0 when the transport is unavailable). */
export function playheadSec(services: Pick<AppServices, 'audio'>): number {
  try {
    return Math.max(0, services.audio.transport.chartTime());
  } catch {
    return 0;
  }
}

/** Current playhead as an f64 beat. */
export function playheadBeat(services: Pick<AppServices, 'audio' | 'store' | 'engine'>): number {
  const tempo = tempoOf(services);
  return tempo === null ? 0 : tempo.beatF64At(playheadSec(services));
}

/** Seek the transport to a beat and keep it visible. */
export function seekToBeat(services: AppServices, beat: number): void {
  const tempo = tempoOf(services);
  if (tempo === null) return;
  services.audio.transport.seek(Math.max(0, tempo.secAtBeatF64(Math.max(0, beat))));
  try {
    services.timeline?.reveal(beat);
  } catch {
    // The timeline may be mid-mount.
  }
}

/** True when a drag or click should ignore snapping (Alt, or Ctrl+Shift in the Alt-free variant). */
export function noSnapModifier(
  services: Pick<AppServices, 'keymap'>,
  e: { readonly alt: boolean; readonly ctrl: boolean; readonly shift: boolean },
): boolean {
  if (e.alt) return true;
  try {
    return services.keymap.altFree && e.ctrl && e.shift;
  } catch {
    return false;
  }
}

/** Finest grid: 1/1920 beat (snap off, or the no-snap modifier). */
export const FREE_DIV = 1920;

/** The beat snap divisor in effect (FREE_DIV when snapping is off or bypassed). */
export function snapDivOf(snap: SnapState, free: boolean): number {
  return free || !snap.beatSnap ? FREE_DIV : Math.max(1, Math.min(FREE_DIV, Math.round(snap.div)));
}

/** A beat (f64) snapped to the grid in effect, never before beat 0. */
export function snapBeatAt(beat: number, snap: SnapState, free: boolean): Beat {
  const b = beatFromF64Snapped(Math.max(0, Number.isFinite(beat) ? beat : 0), snapDivOf(snap, free));
  return Beats.lt(b, Beats.ZERO) ? Beats.ZERO : b;
}

/** RPE X snapped to the X grid when on (else rounded to 0.01 px). */
export function snapXAt(x: number, snap: SnapState, free: boolean): number {
  const v = Number.isFinite(x) ? x : 0;
  if (snap.xSnap && !free && snap.xGrid >= 2) return roundX(snapToXGrid(v, snap.xGrid));
  return roundX(v);
}

export function roundX(x: number): number {
  const r = Math.round(x * 100) / 100;
  return Object.is(r, -0) ? 0 : r;
}

/** One snap step as a Beat (1/div). */
export function snapStepBeat(snap: SnapState): Beat {
  return Beats.make(0, 1, snapDivOf(snap, false));
}

/** Half an X grid step in RPE px (arrow nudges; SEL-09). */
export function xNudgeStep(snap: SnapState): number {
  const n = Math.round(snap.xGrid);
  return n >= 2 ? 675 / (n - 1) : 10;
}

// ---------------------------------------------------------------------------------------------------------------
// Note indexes per line (cached on the notes version topic)
// ---------------------------------------------------------------------------------------------------------------

const indexCache = new WeakMap<object, Map<LineId, { readonly v: number; readonly index: LaneNoteIndex }>>();

/** Lane note index of a line (rebuilt when its notes changed). */
export function noteIndexOf(store: DocStore | null, line: LineId | null): LaneNoteIndex {
  if (store === null || line === null) return EMPTY_NOTE_INDEX;
  let byLine = indexCache.get(store);
  if (byLine === undefined) {
    byLine = new Map();
    indexCache.set(store, byLine);
  }
  const v = store.version(`notes:${String(line)}` as `notes:${number}`);
  const hit = byLine.get(line);
  if (hit?.v === v) return hit.index;
  const notes: Note[] = [];
  for (const id of store.idx.notesByLine(line)) {
    const n = store.doc.notes.get(id);
    if (n !== undefined) notes.push(n);
  }
  const index = buildLaneNoteIndex(notes);
  byLine.set(line, { v, index });
  return index;
}

// ---------------------------------------------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------------------------------------------

/** Display name of a line ("Main", or "Line 3" by export index). */
export function lineLabel(services: Pick<AppServices, 'i18n'>, doc: ChartDoc, idx: DocIndexes, line: LineId): string {
  const rec = doc.lines.get(line);
  if (rec === undefined) return services.i18n.t(m.noLine);
  if (rec.name.trim() !== '') return rec.name;
  return services.i18n.t(m.lineUnnamed, { index: idx.lineIndex(line) });
}

export function kindLabel(services: Pick<AppServices, 'i18n'>, kind: NoteKind): string {
  return services.i18n.t(m.kindName, { kind });
}

/** Beat as the settings show it (colon or plus style; bar style falls back to colon here). */
export function beatText(services: Pick<AppServices, 'settings'>, b: Beat): string {
  let style: 'colon' | 'plus' | 'bar';
  try {
    style = services.settings.value.editing.beatDisplay;
  } catch {
    style = 'colon';
  }
  return formatBeat(b, style === 'plus' ? 'plus' : 'colon');
}

export function beatTextF64(services: Pick<AppServices, 'settings'>, beat: number): string {
  return beatText(services, Beats.fromF64(Math.max(0, beat), FREE_DIV));
}

export function secText(sec: number): string {
  return formatSec(sec);
}

/** Item counts as a translated list ("3 notes, 2 keyframes"). */
export function itemsText(
  services: Pick<AppServices, 'i18n'>,
  counts: { readonly notes?: number; readonly keys?: number; readonly segments?: number; readonly markers?: number },
): string {
  const parts: string[] = [];
  if ((counts.notes ?? 0) > 0) parts.push(services.i18n.t(m.itemsNotes, { count: counts.notes ?? 0 }));
  if ((counts.keys ?? 0) > 0) parts.push(services.i18n.t(m.itemsKeys, { count: counts.keys ?? 0 }));
  if ((counts.segments ?? 0) > 0) parts.push(services.i18n.t(m.itemsSegments, { count: counts.segments ?? 0 }));
  if ((counts.markers ?? 0) > 0) parts.push(services.i18n.t(m.itemsMarkers, { count: counts.markers ?? 0 }));
  if (parts.length === 0) return services.i18n.t(m.itemsNotes, { count: 0 });
  return parts.reduce((a, b) => services.i18n.t(m.listJoin, { a, b }));
}

/** Notes, keys and segments selected now (ids that still exist). */
export function selectedItems(services: Pick<AppServices, 'store' | 'editor'>): {
  readonly notes: NoteId[];
  readonly keys: KeyId[];
  readonly segments: KeyId[];
  readonly markers: MarkerId[];
} {
  const store = services.store;
  const sel = services.editor.getState().selection;
  if (store === null) return { notes: [], keys: [], segments: [], markers: [] };
  const doc = store.doc;
  return {
    notes: [...sel.notes].filter((id) => doc.notes.has(id)),
    keys: [...sel.keys].filter((id) => doc.keys.has(id)),
    segments: [...sel.segments].filter((id) => doc.keys.has(id)),
    markers: [...sel.markers].filter((id) => doc.markers.has(id)),
  };
}

/** The active line when it exists in the document. */
export function activeLineOf(services: Pick<AppServices, 'store' | 'editor'>): LineId | null {
  const store = services.store;
  const line = services.editor.getState().activeLine;
  return store !== null && line !== null && store.doc.lines.has(line) ? line : null;
}

/** Editor state (shortcut). */
export function editorOf(services: Pick<AppServices, 'editor'>): EditorStore {
  return services.editor.getState();
}

/** True when the line is a UI mover (cannot hold notes). */
export function isUiMoverLine(store: DocStore | null, line: LineId | null): boolean {
  if (store === null || line === null) return false;
  return (store.doc.lines.get(line)?.attachUI ?? null) !== null;
}

export function isLockedLine(services: Pick<AppServices, 'editor'>, line: LineId | null): boolean {
  return line !== null && services.editor.getState().filters.lockedLines.has(line);
}

/** Line owning a key's track (line tracks only), or null. */
export function lineOfKey(store: Pick<DocStore, 'doc'>, key: KeyId): LineId | null {
  const k = store.doc.keys.get(key);
  const owner = k === undefined ? undefined : store.doc.tracks.get(k.track)?.owner;
  return owner?.kind === 'line' ? owner.line : null;
}

/** Items an edit would change, by kind (lines: edits that create notes or keys on them, e.g. a paste target). */
export interface EditTargets {
  readonly notes?: Iterable<NoteId>;
  readonly keys?: Iterable<KeyId>;
  readonly lines?: Iterable<LineId>;
}

/**
 * Whether an edit touches a locked line (DESIGN.md §4.10: lock only blocks editing; selecting, copying and viewing
 * still work). Notes count by their line, keys by the line owning their track.
 */
export function touchesLockedLine(services: Pick<AppServices, 'store' | 'editor'>, targets: EditTargets): boolean {
  const locked = services.editor.getState().filters.lockedLines;
  const store = services.store;
  if (locked.size === 0 || store === null) return false;
  for (const line of targets.lines ?? []) if (locked.has(line)) return true;
  for (const id of targets.notes ?? []) {
    const line = store.doc.notes.get(id)?.line;
    if (line !== undefined && locked.has(line)) return true;
  }
  for (const id of targets.keys ?? []) {
    const line = lineOfKey(store, id);
    if (line !== null && locked.has(line)) return true;
  }
  return false;
}

/** `m.reasonLocked` when the edit touches a locked line, else null. */
export function lockedReason(services: Pick<AppServices, 'store' | 'editor'>, targets: EditTargets): MessageRef | null {
  return touchesLockedLine(services, targets) ? ref(m.reasonLocked) : null;
}

/**
 * A beat length as a plain count for sentences ("2", "1/2", "1 1/4"), and whether it reads as singular (at most one
 * beat: "1 beat", "1/2 beat").
 */
export function beatCountText(b: Beat): { readonly text: string; readonly one: 'yes' | 'no' } {
  const one = Beats.cmp(b, Beats.ONE) <= 0 ? 'yes' : 'no';
  if (b.n === 0) return { text: String(b.w), one };
  const frac = `${String(b.n)}/${String(b.d)}`;
  return { text: b.w === 0 ? frac : `${String(b.w)} ${frac}`, one };
}

/** Seconds as a plain decimal for sentences ("1.250"). */
export function secCountText(sec: number): string {
  return (Math.round((Number.isFinite(sec) ? sec : 0) * 1000) / 1000).toFixed(3);
}

/** Keymap label of a command's first binding (e.g. "Ctrl+C", "Cmd+C"), or 'none' when it has no binding. */
export function commandKeysText(services: Pick<AppServices, 'keymap'>, command: string): string {
  try {
    const chord = services.keymap.bindingsFor(command)[0];
    return chord === undefined ? 'none' : services.keymap.label(chord);
  } catch {
    return 'none';
  }
}

/** Keymap label of the Alt modifier ("Alt", "Option" on macOS), for mouse gestures named in sentences. */
export function altModifierText(services: Pick<AppServices, 'keymap'>): string {
  try {
    const label = services.keymap.label('Alt+Wheel');
    const plus = label.indexOf('+');
    return plus > 0 ? label.slice(0, plus) : 'Alt';
  } catch {
    return 'Alt';
  }
}
